import { eq } from '@art-suite/art-core-ts';
import {
  getAllShownOnScopeReportTasks,
  getAllTasksById,
  getScanRootWorkStatusSummary,
  isScanRootShownOnScopeReport,
  scanRootHasScopeChanges,
  ScanRootOptimized,
  WorkStatus,
} from '@kaiizen/kai-scan-template-system';

import { ApiPropertyProject, ApiScan, ApiScanNote, ScanType } from '../ApiSchemas';
import { getNumberOfApprovedScopes } from './scanLib';
import {
  ApprovalContext,
  getOverallTaskStatus,
  getScanPartApprovalStatus,
  getTaskStatusPhase,
  OverallTaskStatus,
  ScanPartApprovalStatus,
} from './ScopeOfWorkLogic';

/**
 * The scope state of the scan relative to other scans in the project.
 * - current: everything in the scope is the most recently approved version (i.e. scope is from the latest approved scan OR from the latest scan as long as there are no scope changes and there was a previous approved scan)
 * - outdated: the scope is approved but is not the latest scan
 * - draft: the scope is a draft (i.e. an unapproved initial scan or change order)
 */
export type ScanScopeState = 'outdated' | 'current' | 'draft';

export type ProjectDerivedState = {
  scanHasScopeChanges: boolean;

  /**
   * Whether the scan has tasks marked as needing rework.
   */
  scanHasTasksNeedingRework: boolean;

  /**
   * Whether the scan is ready for closeout (i.e. all scoped tasks are marked completed)
   */
  scanIsReadyForCloseout: boolean;

  scanIsCloseout: boolean;

  /**
   * Whether the closeout scan has been approved. Only meaningful when scanIsCloseout is true.
   */
  closeoutIsApproved: boolean;

  /**
   * The aggregate work status across all scoped tasks.
   */
  workStatus: WorkStatus;

  /**
   * Whether the scan has scoped tasks.
   */
  scanHasScopedTasks: boolean;

  /**
   * Whether the scan is the latest approved scan.
   */
  scanIsLatestApproved: boolean;

  /**
   * Whether the scan is the latest scan.
   */
  scanIsLatest: boolean;

  /**
   * Whether the scan is a change order. (i.e. has scope changes)
   */
  scanIsChangeOrder: boolean;

  /**
   * Whether the scan is the initial scan.
   */
  scanIsInitial: boolean;

  /**
   * The scope state of the scan relative to other scans in the project.
   */
  scanScopeState: ScanScopeState;

  /**
   * Whether the project has an approved scan.
   */
  haveApprovedScan: boolean;

  /**
   * The approval status of the scan.
   */
  approvalStatusByTaskId: Record<string, ScanPartApprovalStatus>;

  /**
   * Where each task sits in its whole life — see {@link OverallTaskStatus}.
   *
   * Covers every task in the scan, including those not yet on the scope report
   * (they read `new`), so a surface can render a status for anything it shows
   * without having to decide what "no entry" means.
   */
  overallTaskStatusByTaskId: Record<string, OverallTaskStatus>;

  /**
   * Whether the scan is submitted for review by a field agent.
   */
  scanIsSubmittedForReview: boolean;

  /**
   * Whether the scan is submittable by a field agent.
   */
  scanIsSubmittable: boolean;

  /**
   * Whether the scan has data that needs review. (both scanIsLatest and scanHasDataThatNeedsReview must be true)
   */
  scanHasDataThatNeedsReview: boolean;

  /**
   * Whether the scan is reviewable by an admin/manager. (both scanIsSubmittedForReview and scanHasDataThatNeedsReview must be true)
   */
  scanIsReviewable: boolean;

  /**
   * The type of scan derived from the contents of scanData.
   */
  effectiveScanType?: ScanType;

  /**
   * The number of scope revisions approved so far (1 once the initial scope is
   * approved, 2 once the first change order is approved, and so on). A draft or
   * in-review change order is not yet counted here; consumers add 1 themselves
   * to display its prospective version number.
   */
  approvedScopeCount: number;
};

export const getScanIsChangeOrder = (optimizedScanRoot?: ScanRootOptimized, optimizedBaselineScanRoot?: ScanRootOptimized): boolean => {
  return (optimizedScanRoot && optimizedBaselineScanRoot) ? scanRootHasScopeChanges(optimizedScanRoot, optimizedBaselineScanRoot) : false;
};

export const getScanScopeState = (
  scan: ApiScan,
  scanIsLatestApproved: boolean,
  scanIsLatest: boolean,
  scanIsChangeOrder: boolean,
  haveApprovedScan: boolean,
): ScanScopeState => {
  // NOTE: please keep this around for debugging purposes
  // it's a handy way to see why derived values are not updating as expected
  // console.log(
  //   'scan', scan.id, scan.scanLifecycleStatus, scan.scanType,
  //   `scanIsLatestApproved: ${scanIsLatestApproved}`,
  //   `scanIsLatest: ${scanIsLatest}`,
  //   `scanIsChangeOrder: ${scanIsChangeOrder}`,
  //   `haveApprovedScan: ${haveApprovedScan}`,
  // );
  return (haveApprovedScan && scanIsLatest && !scanIsChangeOrder)
    ? 'current'
    : scan.scanLifecycleStatus === 'approved'
    ? 'outdated'
    : 'draft';
};

const getScanIsCloseout = ({
  project,
  latestScan,
  scanIsReadyForCloseout,
  scanHasScopeChanges,
}: {
  project?: ApiPropertyProject;
  latestScan: ApiScan;
  scanIsReadyForCloseout: boolean;
  scanHasScopeChanges: boolean;
}): boolean => {
  if (project) {
    if (project.status === 'completed') return true;
    if (project.status !== 'active') return false;
    if (project.reactivatedAt && latestScan.createdAt) {
      const newlyReactivated = latestScan.createdAt > project.reactivatedAt;
      return scanIsReadyForCloseout && !scanHasScopeChanges && newlyReactivated;
    }
  }
  return scanIsReadyForCloseout && !scanHasScopeChanges;
};

export const getProjectDerivedState = (
  {
    project,
    projectIsEditable = true,
    scan,
    latestApprovedScan,
    firstScan,
    latestScan,
    optimizedScanRoot,
    optimizedBaselineScanRoot,
    scanNotesByScanPartId,
    scans,
  }: {
    project?: ApiPropertyProject;
    projectIsEditable?: boolean;
    scan?: ApiScan;
    latestApprovedScan?: ApiScan;
    firstScan?: ApiScan;
    latestScan?: ApiScan;
    optimizedScanRoot?: ScanRootOptimized;
    optimizedBaselineScanRoot?: ScanRootOptimized;
    scanNotesByScanPartId?: Record<string, ApiScanNote[]>;
    scans?: ApiScan[];
  },
  previousDerivedState?: ProjectDerivedState,
): ProjectDerivedState => {
  if (!scan || !firstScan || !latestScan) {
    return {
      approvalStatusByTaskId: {},
      overallTaskStatusByTaskId: {},
      closeoutIsApproved: false,
      effectiveScanType: undefined,
      haveApprovedScan: false,
      scanHasDataThatNeedsReview: false,
      scanHasScopeChanges: false,
      scanHasScopedTasks: false,
      scanHasTasksNeedingRework: false,
      scanIsChangeOrder: false,
      scanIsInitial: false,
      scanIsLatest: false,
      scanIsLatestApproved: false,
      scanIsReadyForCloseout: false,
      scanIsCloseout: false,
      scanIsReviewable: false,
      scanIsSubmittable: false,
      scanIsSubmittedForReview: false,
      scanScopeState: 'draft',
      approvedScopeCount: 0,
      workStatus: 'notStarted',
    };
  }

  const haveApprovedScan = !!latestApprovedScan;
  const scanIsInitial = firstScan.id === scan.id;
  const scanIsLatest = latestScan.id === scan.id;
  const scanIsLatestApproved = latestApprovedScan?.id === scan.id;

  const workStatusSummary = optimizedScanRoot ? getScanRootWorkStatusSummary(optimizedScanRoot) : undefined;
  const scanHasScopeChanges = getScanIsChangeOrder(optimizedScanRoot, optimizedBaselineScanRoot);
  const scanHasScopedTasks = optimizedScanRoot ? isScanRootShownOnScopeReport(optimizedScanRoot) : false;
  const scanHasTasksNeedingRework = workStatusSummary?.workStatus === 'needsRework';
  const scanIsReadyForCloseout = workStatusSummary?.workStatus === 'completed';
  // A change-request scan keeps reporting as one for as long as it has ever
  // left free drafting - i.e. any status other than 'created', 'dispatched',
  // or 'inProgress'. Two things this must hold through, uniformly, with no
  // special-casing per status:
  //  - still under review in any sense (pendingReview, inReview, reviewed,
  //    correctionsRequested) or approved: its live diff can be reverted all
  //    the way to zero (Cancel Task Changes on its only remaining edit)
  //    without being resolved - it's still awaiting either Cancel Change
  //    Order or approval, and scanHasScopeChanges alone can't tell that apart
  //    from "never was a change order" once the diff is empty. This also
  //    covers approval correctly: scanType stays 'changeRequest' forever once
  //    approved (matching Scan.ts's getScanType, so this display stays
  //    consistent with the persisted field), even if it was emptied before
  //    being approved rather than canceled.
  //  - rejected: also kept, deliberately - a rejected change order is done
  //    just as finally as an approved one and isn't editable at all (so the
  //    Cancel Change Order action must be independently gated on
  //    scanLifecycleStatus - see useCancelChangeOrderAction's canCancel -
  //    rather than relying on this to hide it), but its historical record of
  //    having been a real change-order attempt must not be silently
  //    rewritten away just because it was rejected while a genuine diff
  //    still remained.
  // Still free-drafting (created/dispatched/inProgress, never yet submitted)
  // is the one case that stays fully reactive to the live diff, with no
  // stickiness: a scan that was edited into 'changeRequest' and then
  // reverted before ever being submitted for review has nothing pending that
  // needs an explicit resolution, and must fall back to its ordinary type.
  const scanIsStillFreelyDrafting = scan.scanLifecycleStatus === 'created'
    || scan.scanLifecycleStatus === 'dispatched'
    || scan.scanLifecycleStatus === 'inProgress';
  const scanKeepsChangeRequestType = scan.scanType === 'changeRequest' && !scanIsStillFreelyDrafting;
  const scanIsChangeOrder = scanHasScopeChanges || scanKeepsChangeRequestType;

  const scanScopeState = getScanScopeState(
    scan,
    scanIsLatestApproved,
    scanIsLatest,
    scanIsChangeOrder,
    haveApprovedScan,
  );

  const effectiveScanType = scanIsInitial ? 'initial' : scanIsChangeOrder
    ? 'changeRequest'
    : scanIsReadyForCloseout
    ? 'progressCompleted'
    : scanHasTasksNeedingRework
    ? 'progressRework'
    : 'progress';

  const taskStatusPhase = getTaskStatusPhase({ effectiveScanType });

  // Compute approval status for each task
  let approvalStatusByTaskId: Record<string, ScanPartApprovalStatus> = {};
  let overallTaskStatusByTaskId: Record<string, OverallTaskStatus> = {};
  if (optimizedScanRoot && scanNotesByScanPartId && scan) {
    // Build approval context once (used for all approval logic)
    const baselineTasksById = optimizedBaselineScanRoot ? getAllTasksById(optimizedBaselineScanRoot) : undefined;
    const approvalContext: ApprovalContext = {
      scanId: scan.id,
      scanNotesByScanPartId,
      baselineTasksById,
      defaultContractorsByTrade: optimizedScanRoot.defaultContractorsByTrade,
      baselineDefaultContractorsByTrade: optimizedBaselineScanRoot?.defaultContractorsByTrade,
    };

    const scopedTasks = getAllShownOnScopeReportTasks(optimizedScanRoot);
    for (const task of scopedTasks) {
      approvalStatusByTaskId[task.id] = getScanPartApprovalStatus(task, approvalContext);
    }

    // Overall status covers every task, not just the scoped ones — a task that
    // isn't on the scope report yet is exactly the `empty` rung of the ladder.
    for (const task of Object.values(getAllTasksById(optimizedScanRoot))) {
      overallTaskStatusByTaskId[task.id] = getOverallTaskStatus(task, {
        phase: taskStatusPhase,
        approvalStatus: approvalStatusByTaskId[task.id],
        taskHasNotes: (scanNotesByScanPartId[task.id]?.length ?? 0) > 0,
      });
    }
  }
  if (previousDerivedState?.approvalStatusByTaskId && eq(approvalStatusByTaskId, previousDerivedState.approvalStatusByTaskId)) {
    approvalStatusByTaskId = previousDerivedState.approvalStatusByTaskId;
  }
  if (previousDerivedState?.overallTaskStatusByTaskId && eq(overallTaskStatusByTaskId, previousDerivedState.overallTaskStatusByTaskId)) {
    overallTaskStatusByTaskId = previousDerivedState.overallTaskStatusByTaskId;
  }

  const scanIsSubmittedForReview = scan.scanLifecycleStatus === 'inReview'
    || scan.scanLifecycleStatus === 'pendingReview';

  const scanHasDataThatNeedsReview = scanIsLatest
    && ((scanIsInitial && scanHasScopedTasks)
      || scanHasScopeChanges
      || (scanIsReadyForCloseout && projectIsEditable));

  const scanIsReviewable = scanIsSubmittedForReview
    && scanHasDataThatNeedsReview;

  const scanIsSubmittable = !scanIsSubmittedForReview
    && scanHasDataThatNeedsReview;

  const scanIsCloseout = getScanIsCloseout({
    project,
    latestScan,
    scanIsReadyForCloseout,
    scanHasScopeChanges,
  });

  const closeoutIsApproved = scanIsCloseout && scan.scanLifecycleStatus === 'approved';

  return {
    approvalStatusByTaskId,
    overallTaskStatusByTaskId,
    closeoutIsApproved,
    effectiveScanType,
    haveApprovedScan,
    scanHasDataThatNeedsReview,
    scanHasScopeChanges,
    scanHasScopedTasks,
    scanHasTasksNeedingRework,
    scanIsChangeOrder,
    scanIsInitial,
    scanIsLatest,
    scanIsLatestApproved,
    scanIsReadyForCloseout,
    scanIsCloseout,
    scanIsReviewable,
    scanIsSubmittable,
    scanIsSubmittedForReview,
    scanScopeState,
    approvedScopeCount: getNumberOfApprovedScopes(scans ?? []),
    workStatus: workStatusSummary?.workStatus ?? 'notStarted',
  };
};
